import * as React from 'react';
import {
    AppBar,
    Box,
    Button,
    Dialog, DialogActions, DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    makeStyles,
    MenuItem,
    Popover,
    Toolbar,
    Typography,
    withStyles
} from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import grey from '@material-ui/core/colors/grey';
import clsx from 'clsx';
import { appStore, authService, languageService, measureService } from '../index';
import { observer } from 'mobx-react';
import { MeasureTypes } from '../Services/MeasureService';
import { filtersStore } from '../Stores/FiltersStore';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import { RGL_RESTORE_LAYOUT } from '../AppConstants';
import { FormSelect } from './Form/FormSelect';
import { LanguageService, LNG } from '../Services/LanguageService';

const useStyles = makeStyles((theme) => ({
    title: {
        flexGrow: 1,
    },
    userOpen: {
        background: grey[50],
        color: theme.palette.primary.main,
        cursor: 'pointer',
    },
    log: {
        textTransform: 'none',
        background: grey[50],
    },
    popover: {
        marginTop: theme.spacing(1),
    },
    paper: {
        overflow: 'visible',

        '&::before': {
            content: '\'\'',
            margin: 'auto',
            backgroundColor: grey[50],
            display: 'block',
            width: theme.spacing(1),
            height: theme.spacing(1),
            boxShadow: theme.shadows[1],
            transform: 'rotate(45deg)',
            position: 'absolute',
            right: 10,
            top: -theme.spacing(.5),
        },
    },
    popoverBody: {
        position: 'relative',
        backgroundColor: grey[50],
        borderRadius: theme.spacing(.5),
    },
    logo: {
        marginRight: theme.spacing(1),
    },
    toolbar: {
        minHeight: 60,
        backgroundColor: theme.palette.primary.dark,
        padding: theme.spacing(0, 1),
    },
    subMenu: {
        position: 'absolute',
        right: '100%',
        top: 0,
    },
    clear: {
        position: 'relative',
    },
    filter: {
        fontSize: 20,
    },
    cross: {
        position: 'absolute',
        right: -1,
        top: 8,
        fontSize: 12,
        zIndex: 1,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '50%',
    },
    select: {
        flexDirection: 'column',
        alignItems: 'stretch'
    }
}));
type HeaderNavigationProps = {
    userName?: string;
}

const HeaderIconButton = withStyles({
    root: {
        height: 44,
        marginLeft: 8,
        minWidth: 44,
        '& .MuiSvgIcon-root': {
            fontSize: 28,
        },
    },
})(IconButton);
const SSMMenuItem = withStyles({
    root: {
        fontSize: 16,
    },
})(MenuItem);

export const HeaderNavigation: React.FC<HeaderNavigationProps> = observer(({ userName }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [open, setOpen] = React.useState(false);
    const _handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const _handleClose = () => {
        setAnchorEl(null);
    };

    const _signIn = () => {
        authService.signIn();
        setAnchorEl(null);
    };

    const _signOut = () => {
        authService.signOut();
        setAnchorEl(null);
    };

    const _handleClickOpen = () => {
        setOpen(true);
        _handleClose();
    };

    const _handleDialogClose = () => {
        setOpen(false);
    };

    const _handleMeasureChange = (measure: MeasureTypes) => {
        measureService.setMeasure(measure);
        _handleDialogClose();
    };

    const _handleLangChange = (lang: string) => {
        languageService.setLang(lang);
        _handleDialogClose();
    };

    const _onResetClick = () => {
        document.dispatchEvent(new Event(RGL_RESTORE_LAYOUT));
    };

    const measureOptions = [
        { label: LNG.Text('Dashboard.13'), value: MeasureTypes.Metric },
        { label: LNG.Text('Dashboard.14'), value: MeasureTypes.Imperial },
    ];

    const _navigation = (
        <>
            <HeaderIconButton color="primary" hidden={!filtersStore.resetVisible} onClick={filtersStore.onResetClick}>
                <div className={classes.clear}>
                    <i className={clsx('dx-icon-filter', classes.filter)}/>
                    <i className={clsx('dx-icon-clear', classes.cross)}/>
                </div>
            </HeaderIconButton>

            <HeaderIconButton color="primary" hidden={!appStore.resetGridVisible} onClick={_onResetClick}>
                <SettingsBackupRestoreIcon/>
            </HeaderIconButton>
            <HeaderIconButton
                color="primary"
                onClick={_handleClick}
                className={clsx({ [classes.userOpen]: Boolean(anchorEl) })}
            >
                <AccountCircleIcon/>
            </HeaderIconButton>
            <Popover
                anchorEl={anchorEl}
                classes={{ root: classes.popover, paper: classes.paper }}
                open={Boolean(anchorEl)}
                onClose={_handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box className={classes.popoverBody}>
                    <SSMMenuItem onClick={_handleClickOpen}>{LNG.Text('Dashboard.12')}</SSMMenuItem>
                    <Divider/>
                    <SSMMenuItem onClick={_signOut}>{LNG.Text('Dashboard.11')}</SSMMenuItem>
                </Box>
            </Popover>
            <Dialog onClose={_handleDialogClose} open={open}>
                <DialogTitle>{LNG.Text('Dashboard.10')}</DialogTitle>
                <DialogContent>
                    <FormSelect
                        isSimple={true}
                        className={classes.select}
                        label={LNG.Text('Dashboard.15')}
                        value={measureService.currentMeasure}
                        items={measureOptions}
                        onValueChange={_handleMeasureChange}
                    />
                    <FormSelect
                        isSimple={true}
                        className={classes.select}
                        label={LNG.Text('Dashboard.16')}
                        value={languageService.currentLanguage}
                        items={languageService.options}
                        onValueChange={_handleLangChange}
                    />
                </DialogContent>
                <DialogActions/>
            </Dialog>
        </>
    );

    const logIn = (
        <Button
            variant="contained"
            onClick={_signIn}
            size="large"
            color="default"
            className={classes.log}
        >
            {LNG.Text('Dashboard.9')}
        </Button>
    );

    return (
        <>
            <AppBar position="static">
                <Toolbar className={classes.toolbar}>
                    <img
                        className={classes.logo}
                        src="data:image/png;base64,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"
                        alt={LNG.Text('Dashboard.8')}/>
                    <Typography variant="h6" className={classes.title}>
                        {LNG.Text('Dashboard.7')}
                    </Typography>
                    {userName ? _navigation : logIn}
                </Toolbar>
            </AppBar>
        </>
    );
});